import type { QuestionId } from '../types/ids';

/**
 * Kontrollierter Ergebnistext je Frage und Punktwert (FR-INSIGHT-004).
 * Wird für Stärken- und Engpass-Karten sowie Detailansichten verwendet.
 * Keine Formulierung bewertet die Person als „falsch“ oder „schlecht“.
 */
export interface QuestionResultCopy {
  questionId: QuestionId;
  score: 0 | 1 | 2;
  title: string;
  summary: string;
  detail: string;
  defaultImportance: number;
}

type CopyByScore = Record<0 | 1 | 2, Omit<QuestionResultCopy, 'questionId' | 'score'>>;

const table: Record<QuestionId, CopyByScore> = {
  audience_activity: {
    0: {
      title: 'Zielgruppe auf Instagram kaum aktiv',
      summary: 'Deine konkrete Zielgruppe scheint auf Instagram derzeit kaum aktiv zu sein.',
      detail:
        'Ohne Aktivität der eigentlichen Zielgruppe bleibt Reichweite auf Instagram wirtschaftlich wenig wirksam. Das spricht dafür, die tatsächliche Plattformnutzung genauer zu prüfen oder einen anderen Kanal in Betracht zu ziehen.',
      defaultImportance: 95,
    },
    1: {
      title: 'Zielgruppe teilweise auf Instagram aktiv',
      summary: 'Ein Teil deiner Zielgruppe ist auf Instagram aktiv.',
      detail:
        'Es besteht eine gewisse Erreichbarkeit, aber nicht die gesamte Zielgruppe nutzt die Plattform aktiv. Es lohnt sich zu beobachten, welcher Teil tatsächlich reagiert.',
      defaultImportance: 95,
    },
    2: {
      title: 'Zielgruppe aktiv auf Instagram',
      summary: 'Deine Zielgruppe nutzt Instagram regelmäßig.',
      detail:
        'Das ist eine der wichtigsten Grundvoraussetzungen für einen funktionierenden Kanal: Die Menschen, die du erreichen möchtest, sind tatsächlich vor Ort.',
      defaultImportance: 95,
    },
  },
  feed_relevance: {
    0: {
      title: 'Thema eher suchgetrieben',
      summary: 'Dein Thema wird eher gezielt gesucht als beiläufig im Feed entdeckt.',
      detail:
        'Instagram unterbricht Menschen, statt eine aktive Suche zu bedienen. Bei stark suchgetriebenen Themen können Suchmaschinen oder Video-Suche näher am tatsächlichen Bedarf liegen.',
      defaultImportance: 70,
    },
    1: {
      title: 'Thema teils feedtauglich, teils suchgetrieben',
      summary:
        'Dein Thema eignet sich teilweise für den Feed und teilweise für eine gezielte Suche.',
      detail:
        'Beide Zugänge sind möglich. Instagram kann funktionieren, sollte aber nicht die einzige Quelle für Aufmerksamkeit sein.',
      defaultImportance: 70,
    },
    2: {
      title: 'Thema feedtauglich',
      summary: 'Dein Thema ist auch ohne akute Suche regelmäßig interessant.',
      detail:
        'Das passt sehr gut zur Funktionsweise eines Feeds, in dem Menschen nicht aktiv nach einer Lösung suchen, sondern beiläufig auf Inhalte stoßen.',
      defaultImportance: 70,
    },
  },
  visual_explainability: {
    0: {
      title: 'Thema schwer sichtbar zu machen',
      summary: 'Dein Thema lässt sich aktuell nur schwer visuell darstellen.',
      detail:
        'Ohne eine gute visuelle Übersetzung wird jeder Beitrag erklärungsintensiver. Textbasierte oder audiobasierte Formate könnten hier ergänzend sinnvoll sein.',
      defaultImportance: 65,
    },
    1: {
      title: 'Thema mit Aufwand darstellbar',
      summary: 'Dein Thema lässt sich mit zusätzlichem Aufwand visuell zeigen.',
      detail:
        'Es gibt Wege, das Thema sichtbar zu machen, sie erfordern aber bewusste Vorbereitung und wiederkehrende Formate.',
      defaultImportance: 65,
    },
    2: {
      title: 'Thema gut sichtbar zu machen',
      summary: 'Dein Thema lässt sich sehr gut sichtbar erklären.',
      detail:
        'Das reduziert den Erklärungsaufwand pro Beitrag deutlich und macht die Inhaltserstellung leichter wiederholbar.',
      defaultImportance: 65,
    },
  },
  transformation_visibility: {
    0: {
      title: 'Ergebnis schwer zeigbar',
      summary:
        'Die Veränderung, die dein Angebot bewirkt, ist aktuell schwer sichtbar oder nachvollziehbar.',
      detail:
        'Ein schwer zeigbares Ergebnis erhöht die Unsicherheit vor dem Kauf. Fallbeispiele oder nachvollziehbare Beschreibungen können hier gezielt helfen.',
      defaultImportance: 65,
    },
    1: {
      title: 'Ergebnis erklärbar, aber begrenzt zeigbar',
      summary: 'Die Veränderung lässt sich erklären, aber nur begrenzt zeigen.',
      detail:
        'Beschreibungen und Beispiele können die fehlende direkte Sichtbarkeit teilweise ausgleichen.',
      defaultImportance: 65,
    },
    2: {
      title: 'Ergebnis gut zeigbar',
      summary: 'Die Veränderung lässt sich klar zeigen oder vorführen.',
      detail:
        'Ein sichtbares Ergebnis macht das Angebot greifbarer und senkt die Unsicherheit vor dem Kauf spürbar.',
      defaultImportance: 65,
    },
  },
  topic_depth: {
    0: {
      title: 'Begrenzter Themenvorrat',
      summary: 'Es gibt aktuell nur wenige unterschiedliche Themen für Inhalte.',
      detail:
        'Ein knapper Themenvorrat erschwert die dauerhafte, abwechslungsreiche Umsetzung. Fehler, Fallbeispiele und einzelne Methodenschritte können neue Themen liefern.',
      defaultImportance: 60,
    },
    1: {
      title: 'Themenvorrat vorhanden, aber begrenzt',
      summary: 'Es gibt einige Themen, der Vorrat ist aber noch begrenzt.',
      detail:
        'Für den Start reicht das, langfristig lohnt sich eine bewusste Erweiterung der Themenquellen.',
      defaultImportance: 60,
    },
    2: {
      title: 'Großer Themenvorrat',
      summary: 'Aus deinem Angebot lassen sich dauerhaft viele hilfreiche Inhalte ableiten.',
      detail:
        'Ein großer Themenvorrat erleichtert die regelmäßige und abwechslungsreiche Umsetzung erheblich.',
      defaultImportance: 60,
    },
  },
  trust_relevance: {
    0: {
      title: 'Vertrauen wenig kaufentscheidend',
      summary: 'Vertrauen in dich oder deine Marke ist für den Kauf kaum entscheidend.',
      detail:
        'Das kann die Anforderungen an persönliche Sichtbarkeit senken und eröffnet mehr Spielraum bei der Wahl des Sichtbarkeitsmodells.',
      defaultImportance: 80,
    },
    1: {
      title: 'Vertrauen wichtig, aber nicht allein entscheidend',
      summary: 'Vertrauen spielt eine Rolle, ist aber nicht allein kaufentscheidend.',
      detail:
        'Neben Vertrauen wirken auch andere Faktoren wie Preis oder Nutzen. Eine gute Mischung aus Beleg und Sichtbarkeit ist meist sinnvoll.',
      defaultImportance: 80,
    },
    2: {
      title: 'Vertrauen stark kaufentscheidend',
      summary:
        'Vertrauen in dich, deine Methode oder deine Marke ist für den Kauf besonders wichtig.',
      detail:
        'Ein hoher Vertrauensbedarf erhöht die Bedeutung glaubwürdiger Sichtbarkeit und nachvollziehbarer Belege deutlich.',
      defaultImportance: 80,
    },
  },
  communication_preference: {
    0: {
      title: 'Regelmäßige öffentliche Kommunikation liegt eher fern',
      summary: 'Regelmäßige öffentliche Kommunikation liegt dir aktuell eher fern.',
      detail:
        'Das ist kein Hindernis für Instagram, verlangt aber Formate, die weniger von spontaner Kommunikationsfreude abhängen, etwa vorbereitete Texte oder Grafiken.',
      defaultImportance: 60,
    },
    1: {
      title: 'Kommunikation mit klarer Struktur möglich',
      summary: 'Du kannst regelmäßig kommunizieren, brauchst dafür aber klare Strukturen.',
      detail: 'Feste Formate und Vorlagen können die Umsetzung erheblich erleichtern.',
      defaultImportance: 60,
    },
    2: {
      title: 'Kommunikationsfreude vorhanden',
      summary: 'Du erklärst, zeigst oder erzählst gerne regelmäßig.',
      detail: 'Diese Bereitschaft ist eine gute Grundlage für einen dauerhaft tragfähigen Kanal.',
      defaultImportance: 60,
    },
  },
  public_response_tolerance: {
    0: {
      title: 'Öffentliche Reaktionen belastend',
      summary: 'Schwache Zahlen oder negative Reaktionen könnten dich stark belasten.',
      detail:
        'Klare Grenzen für Kennzahlen und feste Zeiten für Rückmeldungen können helfen, Person und Beitragsleistung bewusst zu trennen.',
      defaultImportance: 60,
    },
    1: {
      title: 'Reaktionen mit bewussten Grenzen tragbar',
      summary: 'Du kannst mit Reaktionen umgehen, musst aber bewusst Grenzen setzen.',
      detail: 'Feste Prüfzeiten und klare Erwartungen können die Belastung weiter reduzieren.',
      defaultImportance: 60,
    },
    2: {
      title: 'Reaktionen gut einordbar',
      summary: 'Du kannst Reichweite und Reaktionen sachlich einordnen.',
      detail:
        'Das ist eine gute Voraussetzung, um auch über schwankende Phasen hinweg dabei zu bleiben.',
      defaultImportance: 60,
    },
  },
  personal_visibility: {
    0: {
      title: 'Persönliche Sichtbarkeit unerwünscht',
      summary: 'Du möchtest möglichst nicht persönlich sichtbar sein.',
      detail:
        'Das ist gut mit einer Methoden- oder Produktmarke vereinbar, kann aber bei hohem Vertrauensbedarf eine bewusste Ergänzung durch Stimme, Beispiele oder Bildschirmaufnahmen benötigen.',
      defaultImportance: 75,
    },
    1: {
      title: 'Begrenzte fachliche Sichtbarkeit möglich',
      summary: 'Begrenzte fachliche Sichtbarkeit ist für dich möglich.',
      detail:
        'Das eröffnet Raum für eine fachlich persönliche Darstellung ohne umfassende private Offenlegung.',
      defaultImportance: 75,
    },
    2: {
      title: 'Persönliche Sichtbarkeit passt gut',
      summary: 'Persönliche fachliche Sichtbarkeit passt gut zu dir.',
      detail:
        'Das ist eine starke Grundlage für einen glaubwürdigen, persönlich geprägten Auftritt.',
      defaultImportance: 75,
    },
  },
  content_system: {
    0: {
      title: 'Zeit und System fehlen aktuell',
      summary: 'Dir fehlen aktuell Zeit und ein verlässliches System für Inhalte.',
      detail:
        'Ohne festen Ablauf wird die Umsetzung schnell unregelmäßig. Ein einfacher, wiederholbarer Ablauf mit festem Zeitfenster kann hier den größten Unterschied machen.',
      defaultImportance: 65,
    },
    1: {
      title: 'Inhalte entstehen, aber noch nicht planbar',
      summary: 'Du kannst Inhalte erstellen, aber noch nicht regelmäßig und planbar.',
      detail:
        'Ein fester Themenblock und gebündelte Produktion können die Regelmäßigkeit deutlich erhöhen.',
      defaultImportance: 65,
    },
    2: {
      title: 'Verlässlicher Ablauf vorhanden',
      summary: 'Du hast feste Zeiten und einen verlässlichen Ablauf für Inhalte.',
      detail:
        'Das ist eine wichtige Grundlage für einen Kanal, der über Monate hinweg tragfähig bleibt.',
      defaultImportance: 65,
    },
  },
  offer_proof: {
    0: {
      title: 'Angebot noch nicht erprobt',
      summary: 'Dein Angebot wurde noch nicht mit echten Kunden erprobt.',
      detail:
        'Bevor viel Zeit in Reichweite investiert wird, lohnt sich eine direkte Prüfung, ob Menschen das konkrete Angebot tatsächlich kaufen möchten.',
      defaultImportance: 95,
    },
    1: {
      title: 'Erste Kaufsignale vorhanden',
      summary: 'Es gab bereits erste Käufe oder klare Interessenssignale.',
      detail:
        'Das ist ein guter Anfang. Eine einfache Zuordnung von Herkunft und Ursache hilft, die weitere Entwicklung sauber einzuschätzen.',
      defaultImportance: 95,
    },
    2: {
      title: 'Angebot erprobt',
      summary: 'Dein Angebot wird bereits wiederholt gekauft.',
      detail:
        'Das ist eine der wichtigsten Grundlagen überhaupt: Ein bereits erprobtes Angebot kann durch Reichweite sinnvoll verstärkt werden.',
      defaultImportance: 95,
    },
  },
  sales_path: {
    0: {
      title: 'Verkaufsweg fehlt',
      summary: 'Es gibt aktuell noch keinen klaren Weg vom Instagram-Inhalt bis zum Kauf.',
      detail:
        'Ohne einen definierten nächsten Schritt bleibt selbst gute Reichweite wirtschaftlich häufig wirkungslos. Ein einzelner, klarer nächster Schritt sollte vor mehr Reichweite priorisiert werden.',
      defaultImportance: 95,
    },
    1: {
      title: 'Verkaufsweg teilweise vorhanden',
      summary: 'Einige Schritte sind vorhanden, der Ablauf ist aber noch nicht vollständig.',
      detail:
        'Eine Ergänzung um Folgeinformationen oder eine klare Produktseite kann den Weg vom Kontakt bis zum Kauf schließen.',
      defaultImportance: 95,
    },
    2: {
      title: 'Verkaufsweg vollständig',
      summary: 'Der Weg vom ersten Kontakt bis zum Kauf ist vollständig aufgebaut.',
      detail:
        'Das ist eine zentrale Voraussetzung, damit Aufmerksamkeit auf Instagram tatsächlich zu Verkäufen führen kann.',
      defaultImportance: 95,
    },
  },
  business_tracking: {
    0: {
      title: 'Wirkung schwer nachvollziehbar',
      summary: 'Du siehst hauptsächlich Likes, Aufrufe und Follower.',
      detail:
        'Ohne einfache Erfassung von Herkunft und Ergebnis bleibt unklar, ob sich der Instagram-Aufwand wirtschaftlich lohnt. Schon eine einfache Tabelle kann hier viel Klarheit schaffen.',
      defaultImportance: 70,
    },
    1: {
      title: 'Anfragen teilweise zuordenbar',
      summary: 'Du kannst Anfragen und neue Kontakte Instagram zuordnen.',
      detail:
        'Das ist ein guter Zwischenschritt. Eine Ergänzung um Verkaufszahlen würde die Einschätzung weiter verbessern.',
      defaultImportance: 70,
    },
    2: {
      title: 'Wirtschaftliche Wirkung nachvollziehbar',
      summary: 'Du kannst nachvollziehen, wie viel Aufwand und Geld zu welchen Verkäufen führt.',
      detail:
        'Das ermöglicht fundierte Entscheidungen darüber, wie viel Instagram-Arbeit sich wirklich lohnt.',
      defaultImportance: 70,
    },
  },
  instagram_independence: {
    0: {
      title: 'Hohe Abhängigkeit von Instagram',
      summary: 'Ohne Instagram könntest du die meisten Interessenten kaum erreichen.',
      detail:
        'Das macht das Geschäft anfällig für Reichweitenschwankungen oder einen zeitweisen Ausfall. Der Aufbau eines zweiten Kontaktwegs, etwa einer E-Mail-Liste, sollte priorisiert werden.',
      defaultImportance: 90,
    },
    1: {
      title: 'Ein weiterer Kontaktweg vorhanden',
      summary: 'Du hast mindestens einen weiteren Weg zu deiner Zielgruppe.',
      detail:
        'Das reduziert die Abhängigkeit bereits spürbar. Ein weiterer Ausbau dieses Wegs erhöht die Stabilität zusätzlich.',
      defaultImportance: 90,
    },
    2: {
      title: 'Gute Unabhängigkeit von Instagram',
      summary: 'Du hast eigene Kontakte und mehrere Wege zu deiner Zielgruppe.',
      detail:
        'Das ist eine wichtige Absicherung: Das Geschäft bleibt auch bei schwankender oder ausbleibender Instagram-Reichweite handlungsfähig.',
      defaultImportance: 90,
    },
  },
};

export function getQuestionResultCopy(
  questionId: QuestionId,
  score: 0 | 1 | 2,
): QuestionResultCopy {
  const entry = table[questionId][score];
  return { questionId, score, ...entry };
}
